import { useMemo } from 'react';
import axios from 'axios';

const MIN_VALIDITY = 70;

const useAxiosKeycloak = (keycloak) => {
  return useMemo(() => {
    if (!keycloak) {
      return axios.create();
    }

    const axiosInstance = axios.create();
    let isRefreshing = false;
    let refreshSubscribers = [];

    const addSubscriber = (callback) => {
      refreshSubscribers.push(callback);
    };

    const notifySubscribers = (error) => {
      refreshSubscribers.forEach((callback) => callback(error));
      refreshSubscribers = [];
    };

    const waitForTokenRefresh = () =>
      new Promise((resolve, reject) => {
        addSubscriber((error) => {
          if (error) {
            reject(error);
          } else {
            resolve();
          }
        });
      });

    const isTokenExpiring = () => {
      if (!keycloak?.tokenParsed?.exp) {
        return true;
      }
      const expiresIn = keycloak.tokenParsed.exp - Math.ceil(Date.now() / 1000);
      return expiresIn < MIN_VALIDITY;
    };

    const refreshToken = async () => {
      if (!keycloak?.updateToken) {
        throw new Error('Cliente de Keycloak no disponible');
      }

      isRefreshing = true;
      try {
        await keycloak.updateToken(MIN_VALIDITY);
        notifySubscribers();
      } catch (error) {
        notifySubscribers(error);
        throw error;
      } finally {
        isRefreshing = false;
      }
    };

    axiosInstance.interceptors.request.use(
      async (config) => {
        if (!keycloak?.authenticated) {
          return Promise.reject(new Error('Usuario no autenticado'));
        }

        try {
          if (isTokenExpiring()) {
            if (!isRefreshing) {
              await refreshToken();
            } else {
              await waitForTokenRefresh();
            }
          } else if (isRefreshing) {
            await waitForTokenRefresh();
          }
        } catch (error) {
          keycloak.logout();
          return Promise.reject(error);
        }

        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${keycloak.token}`;
        return config;
      },
      (error) => Promise.reject(error)
    );

    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            if (!isRefreshing) {
              await refreshToken();
            } else {
              await waitForTokenRefresh();
            }
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
            return axiosInstance(originalRequest);
          } catch (refreshError) {
            keycloak.logout();
            return Promise.reject(refreshError);
          }
        }

        if (!error.response) {
          window.dispatchEvent(new CustomEvent("backend-down"));
        }

        return Promise.reject(error);
      }
    );

    return axiosInstance;
  }, [keycloak]);
};

export default useAxiosKeycloak;
